// PQ-5d — Wardrobe taxonomy for the "Fills a gap" lens.
//
// Static gendered slot definitions. Each slot maps to a predicate over
// item attributes. A hero item "fills a gap" when its resolved slot is
// NOT represented in the user's active+eligible wardrobe.
//
// Positive-only by design: we never surface "you already own one" as a
// negative — that misreads style consolidation (4 blue jeans, 6 white
// shirts) as waste. The lens fires ONLY when a slot is empty.
//
// Predicates are intentionally pragmatic — hero items often carry only
// `category` (silhouette/length/etc. are sparsely populated), so most
// slots resolve from category + light secondary signals. Wardrobe items
// carry richer attributes and can be matched more precisely on the
// ownership check side.

export type Gender = "women" | "men" | "non_binary";

export interface TaxonomyItem {
  category?: string | null;
  material?: string | null;
  formality?: string | null;
  sleeve_length?: string | null;
  silhouette?: string | null;
  length?: string | null;
  style_tags?: string[] | null;
  occasion_tags?: string[] | null;
  name?: string | null;
}

export interface WardrobeSlot {
  id: string;
  /** User-facing label, lowercase natural English. */
  label: string;
  matches: (item: TaxonomyItem) => boolean;
}

// ---------- shared helpers ----------

function cat(i: TaxonomyItem): string {
  return (i.category ?? "").toLowerCase();
}
function mat(i: TaxonomyItem): string {
  return (i.material ?? "").toLowerCase();
}
function form(i: TaxonomyItem): string {
  return (i.formality ?? "").toLowerCase();
}
function tags(i: TaxonomyItem): string[] {
  const out: string[] = [];
  for (const t of i.style_tags ?? []) if (t) out.push(t.toLowerCase());
  for (const t of i.occasion_tags ?? []) if (t) out.push(t.toLowerCase());
  return out;
}
function hasTag(i: TaxonomyItem, ...needles: string[]): boolean {
  const t = tags(i);
  const name = (i.name ?? "").toLowerCase();
  return needles.some((n) => t.some((tag) => tag.includes(n)) || name.includes(n));
}
function isOuter(i: TaxonomyItem): boolean {
  return cat(i) === "outerwear" || cat(i).includes("coat") || cat(i).includes("blazer") || cat(i).includes("jacket");
}
function isTop(i: TaxonomyItem): boolean {
  return cat(i) === "top" || cat(i).includes("shirt") || cat(i).includes("blouse") || cat(i).includes("knit") || cat(i).includes("tee");
}
function isBottom(i: TaxonomyItem): boolean {
  return cat(i) === "bottom" || cat(i).includes("trouser") || cat(i).includes("jean") || cat(i).includes("pant") || cat(i).includes("short") || cat(i).includes("skirt");
}
function isDress(i: TaxonomyItem): boolean {
  return cat(i) === "dress" || cat(i).includes("dress") || cat(i).includes("jumpsuit");
}
function isShoes(i: TaxonomyItem): boolean {
  return cat(i) === "shoes" || cat(i).includes("shoe") || cat(i).includes("boot") || cat(i).includes("trainer") || cat(i).includes("heel") || cat(i).includes("sandal");
}
function isBag(i: TaxonomyItem): boolean {
  return cat(i) === "bag" || cat(i).includes("bag") || cat(i).includes("tote") || cat(i).includes("clutch");
}
function isSmart(i: TaxonomyItem): boolean {
  const f = form(i);
  return f === "business casual" || f === "formal" || f === "dressy" || f === "smart casual";
}
function isCasual(i: TaxonomyItem): boolean {
  const f = form(i);
  return f === "" || f === "casual" || f === "smart casual";
}
function isDressy(i: TaxonomyItem): boolean {
  const f = form(i);
  return f === "formal" || f === "dressy";
}

// ---------- Women's slots ----------

export const WOMEN_SLOTS: WardrobeSlot[] = [
  {
    id: "winter_coat",
    label: "winter coat",
    matches: (i) =>
      isOuter(i) &&
      (mat(i).includes("wool") || mat(i).includes("tweed") || hasTag(i, "coat", "overcoat")),
  },
  {
    id: "smart_blazer",
    label: "smart blazer",
    matches: (i) => isOuter(i) && (isSmart(i) || hasTag(i, "blazer", "tailored")),
  },
  {
    id: "casual_jacket",
    label: "casual jacket",
    matches: (i) => isOuter(i) && isCasual(i) && !hasTag(i, "blazer", "tailored", "coat"),
  },
  {
    id: "day_dress",
    label: "day dress",
    matches: (i) => isDress(i) && !isDressy(i),
  },
  {
    id: "evening_dress",
    label: "evening dress",
    matches: (i) => isDress(i) && isDressy(i),
  },
  {
    id: "white_shirt",
    label: "crisp shirt",
    matches: (i) => isTop(i) && (isSmart(i) || hasTag(i, "shirt", "button", "blouse")),
  },
  {
    id: "knit",
    label: "knit",
    matches: (i) => isTop(i) && (mat(i).includes("knit") || mat(i).includes("wool") || hasTag(i, "knit", "jumper", "sweater")),
  },
  {
    id: "tee",
    label: "t-shirt",
    matches: (i) => isTop(i) && isCasual(i) && (hasTag(i, "tee", "t-shirt") || (i.sleeve_length ?? "").toLowerCase() === "short"),
  },
  {
    id: "denim",
    label: "pair of jeans",
    matches: (i) => isBottom(i) && (mat(i).includes("denim") || hasTag(i, "jean", "denim")),
  },
  {
    id: "smart_trouser",
    label: "pair of smart trousers",
    matches: (i) => isBottom(i) && isSmart(i) && !hasTag(i, "jean", "denim"),
  },
  {
    id: "skirt",
    label: "skirt",
    matches: (i) => isBottom(i) && (cat(i).includes("skirt") || hasTag(i, "skirt")),
  },
  {
    id: "heels",
    label: "pair of heels",
    matches: (i) => isShoes(i) && (isSmart(i) || isDressy(i) || hasTag(i, "heel", "pump")),
  },
  {
    id: "everyday_shoes",
    label: "pair of everyday shoes",
    matches: (i) => isShoes(i) && isCasual(i) && !hasTag(i, "heel"),
  },
  {
    id: "everyday_bag",
    label: "everyday bag",
    matches: (i) => isBag(i),
  },
];

// ---------- Men's slots ----------

export const MEN_SLOTS: WardrobeSlot[] = [
  {
    id: "overcoat",
    label: "overcoat",
    matches: (i) =>
      isOuter(i) && (mat(i).includes("wool") || hasTag(i, "coat", "overcoat")),
  },
  {
    id: "smart_blazer",
    label: "blazer",
    matches: (i) => isOuter(i) && (isSmart(i) || hasTag(i, "blazer", "tailored", "sport coat")),
  },
  {
    id: "casual_jacket",
    label: "casual jacket",
    matches: (i) => isOuter(i) && isCasual(i) && !hasTag(i, "blazer", "tailored", "coat"),
  },
  {
    id: "oxford_shirt",
    label: "oxford shirt",
    matches: (i) => isTop(i) && (isSmart(i) || hasTag(i, "oxford", "shirt", "button-down", "button down")),
  },
  {
    id: "tee",
    label: "t-shirt",
    matches: (i) => isTop(i) && isCasual(i) && ((i.sleeve_length ?? "").toLowerCase() === "short" || hasTag(i, "tee", "t-shirt")),
  },
  {
    id: "knit",
    label: "knit",
    matches: (i) => isTop(i) && (mat(i).includes("knit") || mat(i).includes("wool") || hasTag(i, "knit", "jumper", "sweater")),
  },
  {
    id: "chinos",
    label: "pair of chinos",
    matches: (i) => isBottom(i) && (hasTag(i, "chino") || (isSmart(i) && !mat(i).includes("denim"))),
  },
  {
    id: "denim",
    label: "pair of jeans",
    matches: (i) => isBottom(i) && (mat(i).includes("denim") || hasTag(i, "jean", "denim")),
  },
  {
    id: "smart_trouser",
    label: "pair of dress trousers",
    matches: (i) => isBottom(i) && isDressy(i),
  },
  {
    id: "dress_shoes",
    label: "pair of dress shoes",
    matches: (i) => isShoes(i) && (isSmart(i) || isDressy(i) || hasTag(i, "oxford", "derby", "loafer", "brogue")),
  },
  {
    id: "trainers",
    label: "pair of trainers",
    matches: (i) => isShoes(i) && (isCasual(i) || hasTag(i, "trainer", "sneaker")),
  },
  {
    id: "boots",
    label: "pair of boots",
    matches: (i) => isShoes(i) && hasTag(i, "boot"),
  },
  {
    id: "everyday_bag",
    label: "everyday bag",
    matches: (i) => isBag(i),
  },
];

export function getSlotsForGender(gender: Gender): WardrobeSlot[] {
  switch (gender) {
    case "men":
      return MEN_SLOTS;
    case "women":
      return WOMEN_SLOTS;
    case "non_binary": {
      // Union: dedupe by slot id, preferring women's first-listed copy.
      const seen = new Set<string>();
      const out: WardrobeSlot[] = [];
      for (const s of [...WOMEN_SLOTS, ...MEN_SLOTS]) {
        if (seen.has(s.id)) continue;
        seen.add(s.id);
        out.push(s);
      }
      return out;
    }
  }
}

/** Resolve the slot a given item belongs to. First match wins. */
export function resolveSlotForItem(
  item: TaxonomyItem,
  gender: Gender,
): WardrobeSlot | null {
  for (const slot of getSlotsForGender(gender)) {
    if (slot.matches(item)) return slot;
  }
  return null;
}

/** True when the user owns at least one wardrobe item matching the slot. */
export function userOwnsSlot(
  slot: WardrobeSlot,
  wardrobeItems: TaxonomyItem[],
): boolean {
  return wardrobeItems.some((item) => slot.matches(item));
}
